import { DIAL_CODES } from "../utils/phone.js";
import { Ava } from "../atoms/Ava.jsx";
import { CopySlug } from "../atoms/CopySlug.jsx";
// v1.27.0: Badge import removed — was only used for the party badge.
// The visibility (Public/Private) indicator is inline <span>, not a Badge.

/**
 * Admin Personas list.
 *
 * v1.28.0 (AGCY): gains a tab bar (Officials | Agencies) at the top that
 * filters the list by persona_type. The `personaType` + `onPersonaTypeChange`
 * props are driven from useAppState's adminPersonaTab state, so tab
 * changes re-fire the candidates-fetch effect with the right ?type=
 * filter. When the active tab is empty, we show a friendly empty-state
 * CTA that invites the admin to create their first (agency/official).
 */
export function PersonaList({
  personas, onSelect, onNew, onAnalytics, onProfile,
  personaTotal = 0, personaPage = 0, pageSize = 20, onPageChange,
  personaType = "official",
  onPersonaTypeChange,
}) {
  const total = personaTotal || personas.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = personaPage * pageSize + 1;
  const showingTo = Math.min((personaPage + 1) * pageSize, total);

  // Type-aware copy — same component serves Officials and Agencies tabs.
  const isAgency = personaType === "agency";
  const heading = "Digital Personas";
  const subjectNoun = isAgency ? "agenc" : "active clone";
  const subjectSuffix = isAgency ? (total === 1 ? "y" : "ies") : (total === 1 ? "" : "s");
  const newLabel = isAgency ? "+ New Agency" : "+ New";

  // Tab bar styling — blue underline on active tab, muted on inactive.
  const tabStyle = (active) => ({
    padding: "10px 18px",
    background: "transparent",
    border: "none",
    borderBottom: active ? "2px solid #3b82f6" : "2px solid transparent",
    color: active ? "#60a5fa" : "#64748b",
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "color 0.15s, border-color 0.15s",
  });

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 6 }}>Admin Console</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", margin: 0, fontFamily: "Georgia,serif" }}>{heading}</h1>
          <p style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
            {total} {subjectNoun}{subjectSuffix}{total > pageSize ? ` · showing ${showingFrom}–${showingTo}` : ""}
          </p>
        </div>
        <button onClick={onNew} style={{ padding: "10px 18px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
          {newLabel}
        </button>
      </div>

      {/* v1.28.0 (AGCY): type tabs — Officials | Agencies */}
      <div
        role="tablist"
        aria-label="Persona type"
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 20,
          borderBottom: "1px solid #1e2d45",
        }}
      >
        <button
          role="tab"
          aria-selected={!isAgency}
          onClick={() => onPersonaTypeChange?.("official")}
          style={tabStyle(!isAgency)}
        >
          👥 Officials
        </button>
        <button
          role="tab"
          aria-selected={isAgency}
          onClick={() => onPersonaTypeChange?.("agency")}
          style={tabStyle(isAgency)}
        >
          🏛️ Agencies
        </button>
      </div>

      {/* Empty state — friendly CTA when the active tab has no rows */}
      {total === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "56px 24px",
            background: "#0d1724",
            border: "1px dashed #1e2d45",
            borderRadius: 13,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 14 }}>{isAgency ? "🏛️" : "👥"}</div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia,serif", margin: "0 0 8px" }}>
            {isAgency ? "No agencies yet" : "No elected officials yet"}
          </h2>
          <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.6, margin: "0 auto 20px", maxWidth: 360 }}>
            {isAgency
              ? "Agencies are institutional AI clones — Ministries, Commissions, Boards, and Services that citizens can ask about procedures, eligibility, and how to engage."
              : "Add elected officials to let citizens in their jurisdiction ask questions and learn their positions."}
          </p>
          <button
            onClick={onNew}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {isAgency ? "+ Create your first agency" : "+ Create your first persona"}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {personas.map(p => {
            // CAND-8: Use real aggregated stats from admin endpoint
            const fc = p.feedbackCounts ?? { liked: 0, disliked: 0, flagged: 0 };
            const geoFlag = DIAL_CODES.find(d => d.code === p.geoCountry)?.flag;
            return (
              <div key={p.id} style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 13, padding: 18 }}>
                <div style={{ display: "flex", gap: 13, alignItems: "flex-start", marginBottom: 12 }}>
                  <Ava ini={p.avatar} color={p.accentColor} size={46} photoUrl={p.photoUrl} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
                      <span style={{ color: "#f1f5f9", fontSize: 15, fontWeight: 800, fontFamily: "Georgia,serif" }}>{p.name}</span>
                      <span data-testid="visibility-badge" style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 9, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                        background: p.active ? "#16a34a22" : "#64748b22",
                        color: p.active ? "#22c55e" : "#94a3b8",
                        border: p.active ? "1px solid #16a34a44" : "1px solid #64748b33",
                      }}>{p.active ? "Public" : "Private"}</span>
                      {geoFlag && <span style={{ fontSize: 14 }}>{geoFlag}</span>}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginBottom: 4 }}>{p.role}</div>
                    {(p.geoState || p.geoLga) && <div style={{ color: "#475569", fontSize: 11, marginBottom: 6 }}>{[p.geoState, p.geoLga].filter(Boolean).join(" › ")}</div>}
                    <CopySlug slug={p.slug} color={p.accentColor} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 13, paddingLeft: 2 }}>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}><strong style={{ color: "#e2e8f0" }}>{p.totalSessions ?? 0}</strong> sessions</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}><strong style={{ color: "#e2e8f0" }}>{p.totalQuestions ?? 0}</strong> questions</span>
                  <span style={{ fontSize: 12, color: "#22c55e" }}>👍 {fc.liked ?? 0}</span>
                  <span style={{ fontSize: 12, color: "#ef4444" }}>👎 {fc.disliked ?? 0}</span>
                  <span style={{ fontSize: 12, color: "#f59e0b" }}>⚠ {fc.flagged ?? 0}</span>
                </div>
                <div style={{ display: "flex", gap: 7 }}>
                  <button onClick={() => onProfile(p)} style={{ flex: 1, padding: "8px 0", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 8, color: "#94a3b8", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Profile</button>
                  <button onClick={() => onAnalytics(p)} style={{ flex: 1, padding: "8px 0", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 8, color: "#94a3b8", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Analytics</button>
                  <button onClick={() => onSelect(p)} style={{ flex: 2, padding: "8px 0", background: `${p.accentColor}22`, border: `1px solid ${p.accentColor}44`, borderRadius: 8, color: p.accentColor, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>Chat →</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CAND-9: Pagination controls — hidden on empty state */}
      {total > 0 && totalPages > 1 && onPageChange && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 24 }}>
          <button
            onClick={() => onPageChange(personaPage - 1)}
            disabled={personaPage === 0}
            style={{ padding: "8px 16px", background: personaPage === 0 ? "#0d1724" : "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 8, color: personaPage === 0 ? "#475569" : "#94a3b8", fontSize: 12, fontWeight: 600, cursor: personaPage === 0 ? "default" : "pointer", fontFamily: "inherit" }}
          >
            ← Previous
          </button>
          <span style={{ color: "#64748b", fontSize: 12 }}>Page {personaPage + 1} of {totalPages}</span>
          <button
            onClick={() => onPageChange(personaPage + 1)}
            disabled={personaPage >= totalPages - 1}
            style={{ padding: "8px 16px", background: personaPage >= totalPages - 1 ? "#0d1724" : "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 8, color: personaPage >= totalPages - 1 ? "#475569" : "#94a3b8", fontSize: 12, fontWeight: 600, cursor: personaPage >= totalPages - 1 ? "default" : "pointer", fontFamily: "inherit" }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default PersonaList;
